import { SKY_TARGET_INFO } from "./data/sky-target-info";

let activeSheet: HTMLElement | null = null;

function ensureStyles(): void {
  if (document.querySelector("#night-sky-target-info-styles")) return;
  const style = document.createElement("style");
  style.id = "night-sky-target-info-styles";
  style.textContent = `
    .night-sky-target[data-sky-target] {
      border:1px solid #d4e3ea;
      color:#274b61;
      font:inherit;
      cursor:pointer;
      box-shadow:0 2px 7px rgba(39,74,99,.08);
      transition:transform .12s ease, background .12s ease, box-shadow .12s ease;
    }
    .night-sky-target[data-sky-target]::after { content:"  ⓘ"; font-size:.78em; color:#6d8a9b; }
    .night-sky-target[data-sky-target]:active { transform:scale(.97); background:#e1eef4; }
    .night-sky-target[data-sky-target]:focus-visible { outline:2px solid #3e7897; outline-offset:2px; }
    .night-sky-target-hint { margin:10px 0 2px; color:#708795; font-size:.78rem; line-height:1.5; }
    .night-sky-guide-backdrop {
      position:fixed;
      inset:0;
      z-index:1600;
      display:flex;
      align-items:flex-end;
      justify-content:center;
      background:rgba(17,35,48,.38);
      backdrop-filter:blur(2px);
      -webkit-backdrop-filter:blur(2px);
      animation:nightSkyFadeIn .16s ease-out;
    }
    .night-sky-guide-sheet {
      width:min(680px,100%);
      box-sizing:border-box;
      padding:10px 20px calc(22px + env(safe-area-inset-bottom));
      border-radius:26px 26px 0 0;
      background:#fff;
      color:#173047;
      box-shadow:0 -14px 42px rgba(20,48,68,.2);
      animation:nightSkySheetUp .2s ease-out;
    }
    .night-sky-guide-handle { width:42px; height:5px; border-radius:999px; background:#d6e0e5; margin:2px auto 14px; }
    .night-sky-guide-top { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; }
    .night-sky-guide-kind { display:block; color:#6e8390; font-size:.78rem; margin-bottom:4px; }
    .night-sky-guide-title { margin:0; font-size:1.25rem; color:#173f58; }
    .night-sky-guide-close {
      flex:none;
      width:38px;
      height:38px;
      border:1px solid #d5e2e8;
      border-radius:999px;
      background:#f7fbfd;
      color:#31566d;
      font:inherit;
      font-size:1.25rem;
      line-height:1;
    }
    .night-sky-guide-body { margin:14px 0 2px; color:#425f70; line-height:1.8; font-size:.95rem; }
    @keyframes nightSkyFadeIn { from { opacity:0; } to { opacity:1; } }
    @keyframes nightSkySheetUp { from { transform:translateY(28px); opacity:.75; } to { transform:translateY(0); opacity:1; } }
    @media (min-width: 760px) {
      .night-sky-guide-backdrop { align-items:center; padding:24px; }
      .night-sky-guide-sheet { max-width:520px; border-radius:24px; padding-bottom:22px; }
    }
  `;
  document.head.appendChild(style);
}

function targetName(element: HTMLElement): string | null {
  const text = element.textContent?.trim() ?? "";
  const separator = text.indexOf("：");
  const raw = separator >= 0 ? text.slice(separator + 1).trim() : text;
  const name = raw.replace(/\s*ⓘ\s*$/, "").trim();
  return SKY_TARGET_INFO[name] ? name : null;
}

function closeSheet(): void {
  activeSheet?.remove();
  activeSheet = null;
}

function showInfo(tag: HTMLElement): void {
  const name = targetName(tag);
  if (!name) return;
  const info = SKY_TARGET_INFO[name];
  closeSheet();

  const backdrop = document.createElement("div");
  backdrop.className = "night-sky-guide-backdrop";
  backdrop.setAttribute("role", "presentation");
  backdrop.innerHTML = `
    <section class="night-sky-guide-sheet" role="dialog" aria-modal="true" aria-labelledby="night-sky-guide-title">
      <div class="night-sky-guide-handle" aria-hidden="true"></div>
      <div class="night-sky-guide-top">
        <div><span class="night-sky-guide-kind">${info.kind}</span><h2 class="night-sky-guide-title" id="night-sky-guide-title">${name}</h2></div>
        <button type="button" class="night-sky-guide-close" aria-label="説明を閉じる">×</button>
      </div>
      <p class="night-sky-guide-body"></p>
    </section>`;
  const body = backdrop.querySelector<HTMLElement>(".night-sky-guide-body");
  if (body) body.textContent = info.description;
  backdrop.querySelector<HTMLElement>(".night-sky-guide-close")?.addEventListener("click", closeSheet);
  backdrop.addEventListener("click", (event) => {
    if (event.target === backdrop) closeSheet();
  });
  document.body.appendChild(backdrop);
  activeSheet = backdrop;
  backdrop.querySelector<HTMLButtonElement>(".night-sky-guide-close")?.focus({ preventScroll: true });
}

function enhanceTags(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>(".night-sky-target:not([data-sky-target])").forEach((tag) => {
    const name = targetName(tag);
    if (!name) return;
    tag.dataset.skyTarget = "1";
    tag.setAttribute("role", "button");
    tag.tabIndex = 0;
    tag.setAttribute("aria-label", `${name}の説明を見る`);
  });

  document.querySelectorAll<HTMLElement>(".night-sky-card").forEach((card) => {
    const heading = card.querySelector("h2")?.textContent?.trim();
    if (heading !== "星空の見え方" || card.querySelector(".night-sky-target-hint")) return;
    const firstTargets = card.querySelector(".night-sky-targets");
    if (!firstTargets) return;
    const hint = document.createElement("p");
    hint.className = "night-sky-target-hint";
    hint.textContent = "星座や星のタグをタップすると、簡単な説明が見られます。";
    firstTargets.insertAdjacentElement("beforebegin", hint);
  });
}

ensureStyles();

document.addEventListener("click", (event) => {
  const tag = (event.target as HTMLElement).closest<HTMLElement>(".night-sky-target[data-sky-target]");
  if (tag) showInfo(tag);
});

document.addEventListener("keydown", (event) => {
  if (event.key === "Escape" && activeSheet) {
    closeSheet();
    return;
  }
  if (event.key !== "Enter" && event.key !== " ") return;
  const tag = (event.target as HTMLElement).closest<HTMLElement>(".night-sky-target[data-sky-target]");
  if (!tag) return;
  event.preventDefault();
  showInfo(tag);
});

const observer = new MutationObserver(() => enhanceTags());
observer.observe(document.documentElement, { childList: true, subtree: true });
window.addEventListener("DOMContentLoaded", () => enhanceTags());
enhanceTags();
